/**
 * UserProvider — canonical owner of the current user's `BackendUserWithPlan`.
 *
 * Before this existed: App.tsx fetched the user 4× (mount, checkout
 * success, focus, message-sent) and 5 module views refetched
 * independently. The result was race conditions after plan changes and
 * a stale-quota window after chat messages.
 *
 * After this exists: one fetch on mount, one on focus, plus a listener
 * on the `user-invalidate` bus event so any caller (checkout success,
 * role grant, message sent, profile save) just emits the event and the
 * provider refetches. Consumers read via `useCurrentUser()`.
 */

import {
    createContext, useCallback, useContext, useEffect, useRef, useState,
    type ReactNode,
} from 'react';
import {
    fetchCurrentUserWithPlan,
    type BackendUserWithPlan,
} from '../../userService';
import { on as onAppEvent } from '../events/bus';
import { PageSkeleton } from '../ui/skeleton';
import { captureError } from '../errors/captureError';

type UserContextValue = {
    userData: BackendUserWithPlan | null;
    loading: boolean;
    refetch: () => Promise<void>;
    /** Replace the cached user locally — used by surfaces that mutate
     *  a profile field and want the change visible before the next
     *  server fetch (e.g. a field save). */
    patchLocal: (patch: Partial<BackendUserWithPlan>) => void;
};

const UserContext = createContext<UserContextValue | null>(null);

type Props = {
    children: ReactNode;
    /** Opt-out of the initial fetch — used by tests that seed the
     *  context manually. */
    skipInitialFetch?: boolean;
};

export function UserProvider({ children, skipInitialFetch = false }: Props) {
    const [userData, setUserData] = useState<BackendUserWithPlan | null>(null);
    const [loading, setLoading] = useState(!skipInitialFetch);
    const [hasResolvedOnce, setHasResolvedOnce] = useState(skipInitialFetch);
    const inflightRef = useRef<Promise<void> | null>(null);

    const refetch = useCallback(async () => {
        // De-dupe concurrent refetches — if one is already in flight
        // (e.g. focus + message-sent fire in the same tick), piggyback.
        if (inflightRef.current) return inflightRef.current;
        const p = (async () => {
            try {
                const data = await fetchCurrentUserWithPlan();
                setUserData(data);
            } catch (err) {
                // AuthGate will catch genuine 401s via the interceptor;
                // transient network errors shouldn't clear a good
                // cached copy — but surface to telemetry.
                captureError(err, { scope: 'UserProvider', op: 'refetch' });
            } finally {
                setLoading(false);
                setHasResolvedOnce(true);
                inflightRef.current = null;
            }
        })();
        inflightRef.current = p;
        return p;
    }, []);

    const patchLocal = useCallback((patch: Partial<BackendUserWithPlan>) => {
        setUserData((prev) => (prev ? { ...prev, ...patch } : prev));
    }, []);

    useEffect(() => {
        if (skipInitialFetch) return;
        void refetch();
    }, [skipInitialFetch, refetch]);

    useEffect(() => {
        const onFocus = () => { void refetch(); };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [refetch]);

    useEffect(() => {
        return onAppEvent('user-invalidate', () => { void refetch(); });
    }, [refetch]);

    // Initial bootstrap: until the first fetch resolves we render a
    // generic destination-shaped PageSkeleton rather than letting the
    // App tree render against userData === null. Refetches (focus,
    // bus events) keep the resolved children visible — only the very
    // first resolve gates the skeleton, so we don't flash on focus.
    const isInitialResolve = loading && !hasResolvedOnce;

    return (
        <UserContext.Provider value={{ userData, loading, refetch, patchLocal }}>
            {isInitialResolve ? <PageSkeleton route="generic" withSidebar /> : children}
        </UserContext.Provider>
    );
}

/** Returns the cached user (null while unauthenticated / loading). */
export function useCurrentUser(): BackendUserWithPlan | null {
    const ctx = useContext(UserContext);
    if (!ctx) {
        throw new Error('useCurrentUser must be used inside <UserProvider>');
    }
    return ctx.userData;
}

/** Exposes the provider's imperative controls (refetch, patchLocal) +
 *  `loading` so surfaces can show a spinner until the first fetch lands. */
export function useUserContext(): UserContextValue {
    const ctx = useContext(UserContext);
    if (!ctx) {
        throw new Error('useUserContext must be used inside <UserProvider>');
    }
    return ctx;
}
